import React, { useCallback } from 'react';
import { TableRow, useTheme } from '@mui/material';
import { Row } from '@tanstack/react-table';
import { AvalorTableIds } from '../Table';
import { useAvTableV2Context } from './AvTableV2Provider';
import AvTableV2RowCell from './AvTableV2RowCell';
import { inlineActionsStyle } from './table.utils';

type DataRecord = Record<string, unknown>;

interface AvTableV2RowProps {
  row: Row<DataRecord>;
  isSelected: boolean;
  isActive: boolean;
  enableActiveRowHighlighting?: boolean;
}

const AvTableV2Row = ({ row, isSelected, isActive, enableActiveRowHighlighting = false }: AvTableV2RowProps) => {
  const theme = useTheme();
  const table = useAvTableV2Context();

  const onRowClick = table.options.meta?.onRowClick;
  const disableRowClickNavigation = table.options.meta?.disableRowClickNavigation;

  const handleRowClick = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>) => {
      const target = event.target as HTMLElement;
      if (target.closest('input, button, a')) {
        return;
      }

      onRowClick?.(row.original, event);

      table.setActiveRowId(row.id);
      event.stopPropagation();
    },
    [row, onRowClick, table]
  );

  const isNavigationDisabled = disableRowClickNavigation?.(row.original);
  const isClickable = (enableActiveRowHighlighting || isActive) && !isNavigationDisabled;

  return (
    <TableRow
      hover
      data-row-id={row.id}
      selected={isSelected}
      aria-label={row.id}
      aria-checked={isSelected}
      className={enableActiveRowHighlighting && isActive ? 'active-row' : ''}
      onClick={isClickable ? handleRowClick : undefined}
      sx={isClickable ? { cursor: 'pointer' } : {}}>
      {row.getVisibleCells().map(cell => {
        const isSelectAllHeader = cell.column.id === AvalorTableIds.SelectAllId;
        const rowCellStyle = cell.column.columnDef.meta?.rowCellStyle;
        const dynamicRowCellStyle = typeof rowCellStyle === 'function' ? rowCellStyle?.({ theme, row: row.original }) : rowCellStyle;

        return (
          <AvTableV2RowCell
            key={`${cell.id}-${isSelected}`}
            cell={cell}
            isSelectAllHeader={isSelectAllHeader}
            onClick={
              isSelectAllHeader
                ? e => {
                    e.stopPropagation();
                    row.getToggleSelectedHandler()(e);
                  }
                : undefined
            }
            sx={{
              ...(dynamicRowCellStyle || {}),
              ...(cell.column.id === AvalorTableIds.InlineActionsId ? inlineActionsStyle : {}),
            }}
          />
        );
      })}
    </TableRow>
  );
};

const MemoizedAvTableV2Row = React.memo(AvTableV2Row);

export default MemoizedAvTableV2Row;
